/**
 * =============================================================================
 * loadingBarDom - Barra de carga DOM (mismas imágenes que la LoadingBar Phaser)
 * =============================================================================
 *
 * Usada en AuthUI (login/transiciones) donde no hay Phaser. Mismo arte
 * frame+fill que `LoadingBar` (Phaser). Es DETERMINADA: `setProgress(0..1)`
 * revela el fill cobre de izquierda a derecha. Ver docs/UI_SKINNING.md.
 *
 * NOTA: `loading_frame.png` tiene el interior OPACO, así que el fill cobre va
 * POR ENCIMA del frame (recortado al canal interno) — si fuera detrás quedaría
 * tapado. El reveal se hace con `clip-path` (no se estira el arte; se descubre).
 */

import { loadingBarWidthCss } from './loadingLayout';

const FRAME_URL = '/assets/ui/loading/loading_frame.png';
const FILL_URL = '/assets/ui/loading/loading_fill.png';
// Canal interno del frame donde calza el fill (medido: frame 1933×119, fill 1847×52).
const FILL_INSET_X = 2.2; // %
const FILL_INSET_Y = 28; // %
const FILL_W = 95.6; // %
const FILL_H = 44; // %

export interface DomLoadingBar {
  /** Elemento a insertar en el DOM. */
  el: HTMLDivElement;
  /** Fija el progreso (0..1); revela el fill cobre de izquierda a derecha. */
  setProgress: (p: number) => void;
  /**
   * Avanza suavemente hasta `target` en `durationMs` (ease-out). Para pantallas
   * sin progreso real (ej. la transición al juego), donde igual queremos que la
   * barra "respire" llenándose en vez de quedar vacía.
   */
  autoAdvanceTo: (target: number, durationMs: number) => void;
}

/**
 * Crea una barra de carga DOM determinada. Arranca vacía; llamá `setProgress`
 * con el avance real (0..1). Ancho responsivo (min(600px, 70vw)).
 */
export function createDomLoadingBar(): DomLoadingBar {
  const container = document.createElement('div');
  container.style.position = 'relative';
  container.style.width = loadingBarWidthCss();
  container.style.aspectRatio = '1933 / 119';
  container.style.pointerEvents = 'none';

  const frame = document.createElement('img');
  frame.src = FRAME_URL;
  frame.draggable = false;
  frame.style.position = 'absolute';
  frame.style.inset = '0';
  frame.style.width = '100%';
  frame.style.height = '100%';
  frame.style.display = 'block';
  frame.style.imageRendering = 'auto';

  // Canal interno: recorta el fill al área visible del frame.
  const track = document.createElement('div');
  track.style.position = 'absolute';
  track.style.left = `${FILL_INSET_X}%`;
  // +1px: baja el interior cobre 1px dentro del frame para quedar EXACTO al
  // preload Phaser (LoadingBar con fillOffsetY:1 en GameScene.createLoadingScreen),
  // así la pantalla cuenta→juego y la del primer preload se ven idénticas.
  track.style.top = `calc(${FILL_INSET_Y}% + 1px)`;
  track.style.width = `${FILL_W}%`;
  track.style.height = `${FILL_H}%`;
  track.style.overflow = 'hidden';

  // Fill cobre, revelado por clip-path según el progreso (no se estira: se descubre).
  const fill = document.createElement('img');
  fill.src = FILL_URL;
  fill.draggable = false;
  fill.style.position = 'absolute';
  fill.style.inset = '0';
  fill.style.width = '100%';
  fill.style.height = '100%';
  fill.style.objectFit = 'fill';
  fill.style.transition = 'clip-path 0.25s ease-out';

  track.appendChild(fill);
  // El frame va DEBAJO; el fill (recortado al canal) por encima para que se vea.
  container.appendChild(frame);
  container.appendChild(track);

  let current = 0;
  let rafId = 0;
  const apply = (): void => {
    const hiddenRight = (1 - current) * 100;
    fill.style.clipPath = `inset(0 ${hiddenRight}% 0 0)`;
  };
  const setProgress = (p: number): void => {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
    fill.style.transition = 'clip-path 0.25s ease-out';
    current = Math.max(current, Math.min(1, Math.max(0, p))); // monotónico (no retrocede)
    apply();
  };
  const autoAdvanceTo = (target: number, durationMs: number): void => {
    if (rafId) cancelAnimationFrame(rafId);
    fill.style.transition = 'none'; // manejamos cada frame nosotros
    const start = current;
    const t0 = performance.now();
    const step = (now: number): void => {
      const k = Math.min(1, (now - t0) / durationMs);
      const eased = 1 - Math.pow(1 - k, 2); // ease-out
      current = Math.max(current, start + (target - start) * eased);
      apply();
      rafId = k < 1 ? requestAnimationFrame(step) : 0;
    };
    rafId = requestAnimationFrame(step);
  };
  setProgress(0);

  return { el: container, setProgress, autoAdvanceTo };
}
